/**
 * paymentAgentReadinessMessages.js
 *
 * Centralne mapowanie kodów `BlockingReason` z backendu Global Readiness Gate
 * (Sprint 1, E4) na czytelne teksty UI.
 *
 * Backend jest jedynym źródłem prawdy dla LOGIKI gotowości. Ten plik dostarcza
 * WYŁĄCZNIE prezentacyjne teksty i grupowanie kodów w wizualne stany
 * (ready / preparing / not_ready / no_new_data).
 *
 * Zasada:
 *   - żadnej logiki decyzyjnej — tylko lookup po kodzie,
 *   - pojedyncze źródło prawdy dla całego UI (banner + toast + tooltip),
 *   - fallback dla nieznanych kodów: pokazujemy generyczny komunikat.
 *
 * Kody odpowiadają enum `services.payment_analysis_readiness.BlockingReason`.
 */

// ─── Wizualny bucket kodu (używany przez PaymentAgentReadinessStatus). ────
export const READINESS_BUCKET = {
  PREPARING: "preparing", // 🟠 trwa poprawny proces
  NOT_READY: "not_ready", // 🔴 wymaga działania user/systemu
  NO_NEW_DATA: "no_new_data", // ⚪ tylko brak danych do przeanalizowania
};

/** Kod → { message, bucket }. */
const REASONS = {
  // KSeF import
  KSEF_SCOPE_NOT_COMPLETED: {
    message: "Import KSeF nie został jeszcze zakończony",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  KSEF_IMPORT_RUNNING: {
    message: "Trwa import faktur z KSeF",
    bucket: READINESS_BUCKET.PREPARING,
  },
  KSEF_IMPORT_WAITING: {
    message: "Import KSeF wstrzymany — limit KSeF, czekamy na okno",
    bucket: READINESS_BUCKET.PREPARING,
  },
  KSEF_IMPORT_PARTIAL: {
    message: "Import KSeF zakończył się częściowo — niektóre dni nie zostały pobrane",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  KSEF_IMPORT_FAILED: {
    message: "Import KSeF zakończył się błędem",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  KSEF_IMPORT_INTERRUPTED: {
    message: "Import KSeF został przerwany",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  KSEF_IMPORT_PAUSED: {
    message: "Import KSeF został wstrzymany",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  KSEF_IMPORT_CANCELLED_PARTIAL: {
    message: "Import KSeF anulowany przed ukończeniem",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  KSEF_SYNC_RUNNING: {
    message: "Trwa sprawdzanie nowych faktur w KSeF",
    bucket: READINESS_BUCKET.PREPARING,
  },
  // Statement / Bank Agent
  NO_STATEMENT: {
    message: "Brak wyciągu bankowego — dodaj wyciąg, żeby zacząć analizę",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  STATEMENT_ANALYSIS_RUNNING: {
    message: "Trwa analiza wyciągu bankowego",
    bucket: READINESS_BUCKET.PREPARING,
  },
  STATEMENT_ANALYSIS_FAILED: {
    message: "Analiza wyciągu zakończyła się błędem",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  STATEMENT_ANALYSIS_STALE: {
    message: "Wyciąg wymaga ponownej analizy (nowe dane)",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  STATEMENT_ANALYSIS_NOT_STARTED: {
    message: "Wyciąg wymaga analizy — uruchom Agenta Wyciągów",
    bucket: READINESS_BUCKET.NOT_READY,
  },
  // Agents / jobs
  BANK_AGENT_RUNNING: {
    message: "Trwa analiza Agenta Wyciągów",
    bucket: READINESS_BUCKET.PREPARING,
  },
  INVOICE_AGENT_RUNNING: {
    message: "Trwa analiza Agenta Faktur",
    bucket: READINESS_BUCKET.PREPARING,
  },
  PAYMENT_ANALYSIS_RUNNING: {
    message: "Analiza płatności jest już w toku",
    bucket: READINESS_BUCKET.PREPARING,
  },
  PAYMENT_ANALYSIS_ACTIVE_RUN: {
    message: "Trwa uruchamianie analizy płatności",
    bucket: READINESS_BUCKET.PREPARING,
  },
  // Idempotency
  CURRENT_GENERATION_ALREADY_ANALYZED: {
    message: "Brak nowych danych do analizy",
    bucket: READINESS_BUCKET.NO_NEW_DATA,
  },
  // Meta
  READINESS_CHECK_FAILED: {
    message: "Nie udało się sprawdzić gotowości — spróbuj za chwilę",
    bucket: READINESS_BUCKET.NOT_READY,
  },
};

/** Zwraca `{ code, message, bucket }` dla dowolnego kodu (fallback dla unknown). */
export function describeBlockingReason(code) {
  const entry = REASONS[code];
  if (entry) return { code, ...entry };
  return {
    code,
    message: code ? `Nieznany status: ${code}` : "Nieznany status",
    bucket: READINESS_BUCKET.NOT_READY,
  };
}

/**
 * Wybiera "główny" blocker do wyświetlenia w banerze.
 *
 * Preferencja (od najbardziej priorytetowego do najmniej):
 *   1. NOT_READY (wymaga działania user/systemu) — najważniejszy.
 *   2. PREPARING (trwa poprawny proces).
 *   3. NO_NEW_DATA (brak nowych danych do przeanalizowania).
 *
 * W obrębie tego samego bucketa — pierwszy z listy wygrywa (zachowuje
 * kolejność backendu, który zwraca blockery w porządku sprawdzania).
 */
export function pickPrimaryReason(blockingReasons = []) {
  if (!Array.isArray(blockingReasons) || blockingReasons.length === 0) return null;
  const described = blockingReasons.map(describeBlockingReason);
  const notReady = described.find((d) => d.bucket === READINESS_BUCKET.NOT_READY);
  if (notReady) return notReady;
  const preparing = described.find((d) => d.bucket === READINESS_BUCKET.PREPARING);
  if (preparing) return preparing;
  return described[0];
}

/**
 * Wizualny stan całego banera na podstawie readiness response:
 *   ready:true                            → 'ready'
 *   automation_enabled + preparing bucket → 'automation_waiting'
 *   NO_NEW_DATA (tylko)                   → 'no_new_data'
 *   preparing (dowolny)                   → 'preparing'
 *   NOT_READY (dowolny)                   → 'not_ready'
 */
export function computeReadinessVisualState(readiness) {
  if (!readiness) return { state: "loading", primary: null };
  if (readiness.ready) return { state: "ready", primary: null };
  const primary = pickPrimaryReason(readiness.blocking_reasons || []);
  if (!primary) return { state: "not_ready", primary: null };

  const buckets = new Set((readiness.blocking_reasons || []).map(
    (c) => describeBlockingReason(c).bucket,
  ));

  // Sam CURRENT_GENERATION_ALREADY_ANALYZED — traktujemy jak „brak nowych danych".
  if (buckets.size === 1 && buckets.has(READINESS_BUCKET.NO_NEW_DATA)) {
    return { state: "no_new_data", primary };
  }

  // Automation enabled + trwa poprawny proces → specjalny komunikat.
  if (
    readiness.automation_enabled &&
    buckets.has(READINESS_BUCKET.PREPARING) &&
    !buckets.has(READINESS_BUCKET.NOT_READY)
  ) {
    return { state: "automation_waiting", primary };
  }

  if (buckets.has(READINESS_BUCKET.NOT_READY)) {
    return { state: "not_ready", primary };
  }
  return { state: "preparing", primary };
}
